import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type TransitionEvent,
} from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "@tanstack/react-router";
import { useLatestRef } from "@/shared/hooks/useLatestRef";
import {
  MIN_SECURITY_NAV_QUERY_LENGTH,
  useSecurityNavSearch,
} from "@/shared/hooks/useSecurityNavSearch";
import HeaderSearchResults, {
  HEADER_SEARCH_LIST_ID,
  headerSearchOptionId,
} from "@/shared/ui/appHeader/HeaderSearchResults";
import {
  SearchSizer,
  Search,
  SearchIconWrapper,
  SearchInputRow,
  SearchResultsClip,
  StyledInputBase,
  SEARCH_ACTIVE_FULL_BLEED_MAX_PX,
  SEARCH_ACTIVE_MAX_WIDTH_PX,
  SEARCH_BACKDROP_BLUR,
  SEARCH_MORPH_DURATION_MS,
  SEARCH_RESTING_MAX_WIDTH_PX,
  SEARCH_RESTING_MIN_WIDTH_PX,
  SEARCH_PANEL_GUTTER_PX,
  headerSearchFittedWidthPx,
} from "@/shared/ui/appHeader/HeaderSearch.styles";
import { measureTextWidth } from "@/shared/utils/measureText";

const SEARCH_PLACEHOLDER = "search cusip, issuer, prf, put, convertible…";
const HEADER_SEARCH_BACKDROP_ID = "header-search-backdrop";

type Phase = "idle" | "from" | "open" | "to";

interface BoxRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface PanelPoint {
  top: number;
  left: number;
  width: number;
}

function measureInputText(input: HTMLInputElement, text: string): number {
  const style = getComputedStyle(input);
  return measureTextWidth(text, { font: style.font, letterSpacing: style.letterSpacing });
}

function readRect(node: HTMLElement): BoxRect {
  const rect = node.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

/** Toolbar content box: the bar's width inside its horizontal padding. */
function readToolbarContent(from: HTMLElement | null): { left: number; width: number } | null {
  const toolbar = from?.closest("[data-app-header-toolbar]");
  if (!(toolbar instanceof HTMLElement)) return null;
  const rect = toolbar.getBoundingClientRect();
  const style = getComputedStyle(toolbar);
  const padLeft = Number.parseFloat(style.paddingLeft) || 0;
  const padRight = Number.parseFloat(style.paddingRight) || 0;
  const width = rect.width - padLeft - padRight;
  if (width <= 0) return null;
  return { left: rect.left + padLeft, width };
}

/**
 * Active field. Above 740px it is a centered bar capped at 680px.
 * At 740px and below it fills the toolbar, staying inside that bar's padding.
 * The top stays on the field so the input row does not jump down.
 */
function headerSearchPanelPoint(
  originTop: number,
  viewportWidth: number,
  toolbar: { left: number; width: number } | null,
): PanelPoint {
  if (viewportWidth <= SEARCH_ACTIVE_FULL_BLEED_MAX_PX && toolbar) {
    return { top: originTop, left: toolbar.left, width: toolbar.width };
  }
  const available = Math.max(viewportWidth - SEARCH_PANEL_GUTTER_PX * 2, 0);
  // The open field draws a 1px border on each side. The inner row is the
  // measured width, so the border box is 2px wider than 680.
  const width = Math.min(SEARCH_ACTIVE_MAX_WIDTH_PX + 2, available);
  const left = Math.max(SEARCH_PANEL_GUTTER_PX, (viewportWidth - width) / 2);
  return { top: originTop, left, width };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function motionStyle(phase: Phase, origin: BoxRect, target: PanelPoint): CSSProperties {
  const atCenter = phase === "open";
  const top = atCenter ? target.top : origin.top;
  const left = atCenter ? target.left : origin.left;
  const width = atCenter ? target.width : origin.width;
  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
}

function resolveFieldStyle(
  overlayOpen: boolean,
  phase: Phase,
  origin: BoxRect | null,
  target: PanelPoint | null,
  openWidth: number | null,
): CSSProperties | undefined {
  if (overlayOpen && origin && target) return motionStyle(phase, origin, target);
  if (openWidth == null) return undefined;
  return { width: `${openWidth}px` };
}

function activeOptionId(
  overlayOpen: boolean,
  itemCount: number,
  activeIndex: number,
): string | undefined {
  if (!overlayOpen || itemCount === 0) return undefined;
  return headerSearchOptionId(Math.min(activeIndex, itemCount - 1));
}

function nextActiveIndex(key: string, current: number, itemCount: number): number | null {
  if (key === "ArrowDown") return Math.min(current + 1, itemCount - 1);
  if (key === "ArrowUp") return Math.max(current - 1, 0);
  return null;
}

/** The portal host sits in the toolbar slot at rest and on `body` while open. */
function applyHostLayout(host: HTMLDivElement, overlayOpen: boolean) {
  if (overlayOpen) {
    host.style.display = "block";
    host.style.width = "0";
    host.style.height = "0";
    host.style.minWidth = "0";
    host.style.justifyContent = "";
    host.style.flex = "";
    return;
  }
  host.style.display = "flex";
  host.style.justifyContent = "flex-end";
  host.style.width = "100%";
  host.style.height = "";
  host.style.minWidth = "0";
  host.style.flex = "1 1 auto";
}

interface ClearSearchButtonProps {
  visible: boolean;
  onClear: () => void;
}

function ClearSearchButton({ visible, onClear }: Readonly<ClearSearchButtonProps>) {
  if (!visible) return null;
  return (
    <IconButton
      aria-label="Clear search"
      disableRipple
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClear}
      sx={(theme) => ({
        flexShrink: 0,
        p: "2px 4px",
        minWidth: 0,
        borderRadius: "4px",
        lineHeight: 1,
        color: theme.app.headerSearch.closeColor,
        "&:hover": {
          color: theme.app.headerSearch.nameColor,
          backgroundColor: "transparent",
        },
        "&:focus-visible, &.Mui-focusVisible": {
          outline: `2px solid ${theme.app.headerSearch.activeColor}`,
          outlineOffset: "2px",
        },
      })}
    >
      <CloseIcon sx={{ fontSize: 18 }} />
    </IconButton>
  );
}

interface HeaderSearchBackdropProps {
  closing: boolean;
  onDismiss: () => void;
}

function HeaderSearchBackdrop({ closing, onDismiss }: Readonly<HeaderSearchBackdropProps>) {
  return (
    <Box
      data-testid={HEADER_SEARCH_BACKDROP_ID}
      aria-hidden
      onMouseDown={(event) => {
        event.preventDefault();
        onDismiss();
      }}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal,
        backgroundColor: (theme) => alpha(theme.palette.common.black, 0.62),
        backdropFilter: SEARCH_BACKDROP_BLUR,
        WebkitBackdropFilter: SEARCH_BACKDROP_BLUR,
        // Stay at full opacity for hit testing. The keyframes fade the scrim in;
        // the close path fades it out with the card.
        opacity: closing ? 0 : 1,
        transition: `opacity ${SEARCH_MORPH_DURATION_MS}ms ease-in-out`,
        animation: closing
          ? "none"
          : `headerSearchBackdropIn ${SEARCH_MORPH_DURATION_MS}ms ease-in-out backwards`,
        "@keyframes headerSearchBackdropIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          transition: "none",
        },
      }}
    />
  );
}

export default function HeaderSearchField() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hugging, setHugging] = useState(false);
  const [openWidth, setOpenWidth] = useState<number | null>(null);
  const [placeholderWidth, setPlaceholderWidth] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [origin, setOrigin] = useState<BoxRect | null>(null);
  const [target, setTarget] = useState<PanelPoint | null>(null);
  const [host] = useState(() => document.createElement("div"));
  const inputRef = useRef<HTMLInputElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const phaseRef = useLatestRef(phase);
  // Only keyboard moves scroll the list; a hovered row is already in view.
  const scrollActiveRef = useRef(false);
  const trimmed = query.trim();
  const resultsVisible = trimmed.length > 0;
  // Focus lifts the field over a scrim. Logo, firm name, and nav stay put and
  // read as dimmed until the field closes.
  const panelOpen = focused;
  const overlayOpen = phase !== "idle" && origin !== null && target !== null;
  // Keep the last hits on screen while the card animates shut. Disabling the
  // query as soon as focus drops would blank the rows mid-flight.
  const search = useSecurityNavSearch(query, { enabled: focused || overlayOpen });

  const clampRestingWidth = (desired: number) => {
    const available = sizerRef.current?.clientWidth ?? 0;
    const bounded = Math.min(
      SEARCH_RESTING_MAX_WIDTH_PX,
      Math.max(SEARCH_RESTING_MIN_WIDTH_PX, desired),
    );
    // A toolbar narrower than the floor keeps the logo on screen.
    if (available <= 0) return bounded;
    return Math.min(bounded, available);
  };

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const next = headerSearchFittedWidthPx(measureInputText(input, SEARCH_PLACEHOLDER));
    setPlaceholderWidth((current) => (current === next ? current : next));
    if (!hugging) {
      const resting = clampRestingWidth(next);
      setOpenWidth((current) => (current === resting ? current : resting));
    }
  }, [hugging]);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    // Move the same host node. Changing a portal container remounts the input
    // and drops the keystroke that opened the card.
    const parent = overlayOpen ? document.body : slot;
    if (host.parentElement !== parent) parent.appendChild(host);
    applyHostLayout(host, overlayOpen);
  }, [host, overlayOpen]);

  // The host can sit on `body` when the field unmounts mid-animation, for
  // example when a route hides the header search.
  useLayoutEffect(() => () => host.remove(), [host]);

  useLayoutEffect(() => {
    if (panelOpen && phase === "to") {
      // After layout, so the morph phase follows the measured rect rather than the render that opened it.
      // oxlint-disable-next-line react/set-state-in-effect
      setPhase("open");
      return;
    }
    if (panelOpen && phase === "idle") {
      const node = searchRef.current;
      if (!node) return;
      const nextOrigin = readRect(node);
      setOrigin(nextOrigin);
      setTarget(
        headerSearchPanelPoint(
          nextOrigin.top,
          window.innerWidth,
          readToolbarContent(sizerRef.current),
        ),
      );
      setPhase(prefersReducedMotion() ? "open" : "from");
      return;
    }
    if (!panelOpen && (phase === "open" || phase === "from")) {
      if (prefersReducedMotion()) {
        setPhase("idle");
        return;
      }
      const ghost = ghostRef.current;
      if (ghost) setOrigin(readRect(ghost));
      setPhase("to");
    }
  }, [panelOpen, phase]);

  useEffect(() => {
    if (phase !== "from") return undefined;
    // Paint the field at its toolbar rect, then let left/width transition.
    const frame = window.requestAnimationFrame(() => {
      setPhase((current) => (current === "from" ? "open" : current));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [phase]);

  useEffect(() => {
    if (phase !== "open") return undefined;
    const place = () => {
      setTarget((current) =>
        headerSearchPanelPoint(
          current?.top ?? 0,
          window.innerWidth,
          readToolbarContent(sizerRef.current),
        ),
      );
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [phase]);

  useEffect(() => {
    if (phase !== "to") return undefined;
    const timeout = window.setTimeout(() => {
      setPhase((current) => (current === "to" ? "idle" : current));
    }, SEARCH_MORPH_DURATION_MS + 80);
    return () => window.clearTimeout(timeout);
  }, [phase]);

  useLayoutEffect(() => {
    if (!panelOpen || !overlayOpen) return;
    const input = inputRef.current;
    if (!input || document.activeElement === input) return;
    input.focus();
  }, [panelOpen, overlayOpen]);

  useEffect(() => {
    // After the query text commits, so the previous highlight is not cleared in that same render.
    // oxlint-disable-next-line react/set-state-in-effect
    setActiveIndex(0);
  }, [trimmed]);

  useEffect(() => {
    if (!scrollActiveRef.current) return;
    scrollActiveRef.current = false;
    document
      .getElementById(headerSearchOptionId(activeIndex))
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  const collapseToRest = () => {
    const input = inputRef.current;
    const raw = input?.value ?? "";
    if (input && raw.trim().length > 0) {
      const fitted = headerSearchFittedWidthPx(measureInputText(input, raw));
      setOpenWidth(clampRestingWidth(fitted));
      setHugging(true);
    } else if (placeholderWidth != null) {
      setOpenWidth(clampRestingWidth(placeholderWidth));
      setHugging(false);
    }
  };

  const closeOverlay = () => {
    setFocused(false);
    collapseToRest();
  };

  const focusLeftSearch = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return false;
    const active = document.activeElement;
    if (active instanceof Node && event.currentTarget.contains(active)) return false;
    return true;
  };

  const collapseIfFocusLeft = (event: FocusEvent<HTMLDivElement>) => {
    if (!focusLeftSearch(event)) return;
    closeOverlay();
  };

  const openSecurity = (cusip: string) => {
    closeOverlay();
    inputRef.current?.blur();
    void navigate({ to: `/markets?bondCusip=${encodeURIComponent(cusip)}` });
  };

  const dismiss = () => {
    closeOverlay();
    inputRef.current?.blur();
  };

  const handlePanelTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== "width" && event.propertyName !== "left") return;
    if (phaseRef.current !== "to") return;
    setPhase("idle");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      dismiss();
      return;
    }
    if (!focused || search.items.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      scrollActiveRef.current = true;
      const { key } = event;
      const count = search.items.length;
      setActiveIndex((current) => nextActiveIndex(key, current, count) ?? current);
      return;
    }
    if (event.key === "Enter") {
      const hit = search.items[activeIndex];
      if (!hit) return;
      event.preventDefault();
      openSecurity(hit.cusip);
    }
  };

  const fieldStyle = resolveFieldStyle(overlayOpen, phase, origin, target, openWidth);

  const idleLabel =
    trimmed.length < MIN_SECURITY_NAV_QUERY_LENGTH ? "Type at least 2 characters" : undefined;

  const activeOption = activeOptionId(overlayOpen, search.items.length, activeIndex);
  const resultsOpen = overlayOpen && resultsVisible;

  const panel = (
    <Search
      ref={searchRef}
      overlay={overlayOpen}
      withResults={resultsVisible}
      data-header-search-panel=""
      style={fieldStyle}
      onKeyDown={handleKeyDown}
      onBlur={collapseIfFocusLeft}
      onTransitionEnd={handlePanelTransitionEnd}
    >
      <SearchInputRow
        overlay={overlayOpen}
        withResults={resultsVisible}
        onClick={() => inputRef.current?.focus()}
      >
        <SearchIconWrapper withResults={resultsVisible}>
          <SearchIcon />
        </SearchIconWrapper>
        <StyledInputBase
          inputRef={inputRef}
          value={query}
          placeholder={SEARCH_PLACEHOLDER}
          onChange={(event) => {
            setQuery(event.target.value);
            setFocused(true);
          }}
          onFocus={() => {
            setFocused(true);
          }}
          inputProps={{
            role: "combobox",
            "aria-label": "search",
            "aria-autocomplete": "list",
            "aria-expanded": resultsOpen,
            "aria-controls": resultsOpen ? HEADER_SEARCH_LIST_ID : undefined,
            "aria-activedescendant": activeOption,
          }}
        />
        <ClearSearchButton
          visible={focused && query.length > 0}
          onClear={() => {
            setQuery("");
            inputRef.current?.focus();
          }}
        />
      </SearchInputRow>
      {resultsOpen ? (
        <SearchResultsClip expanded={phase === "open"}>
          <div>
            <HeaderSearchResults
              items={search.items}
              totalCount={search.totalCount}
              totalCountApproximate={search.totalCountApproximate}
              totalCountIsLowerBound={search.totalCountIsLowerBound}
              activeIndex={Math.min(activeIndex, Math.max(search.items.length - 1, 0))}
              isLoading={search.isLoading}
              isError={search.isError}
              idleLabel={idleLabel}
              onActiveIndex={setActiveIndex}
              onSelect={openSecurity}
            />
          </div>
        </SearchResultsClip>
      ) : null}
    </Search>
  );

  const backdrop = <HeaderSearchBackdrop closing={phase === "to"} onDismiss={dismiss} />;

  return (
    <SearchSizer ref={sizerRef} data-search-open={overlayOpen ? "" : undefined}>
      <Box
        ref={slotRef}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: overlayOpen ? 0 : "100%",
          minWidth: 0,
        }}
      />
      {overlayOpen && origin ? (
        <Box
          ref={ghostRef}
          aria-hidden
          sx={{ width: origin.width, height: origin.height, flexShrink: 0, visibility: "hidden" }}
        />
      ) : null}
      {overlayOpen ? createPortal(backdrop, document.body) : null}
      {createPortal(panel, host)}
    </SearchSizer>
  );
}
